import { Component, Input, Output, EventEmitter, forwardRef, ChangeDetectionStrategy, ChangeDetectorRef,
         ContentChildren, TemplateRef, AfterContentInit, QueryList, Inject, Optional, ViewChildren } from '@angular/core';
import { DOCUMENT } from '@angular/platform-browser';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { ENTER, SPACE, UP_ARROW, DOWN_ARROW } from '@angular/material';

import { TdDataTableRowComponent } from './data-table-row/data-table-row.component';
import { ITdDataTableSortChangeEvent } from './data-table-column/data-table-column.component';
import { TdDataTableTemplateDirective } from './directives/data-table-template.directive';

const /** @type {?} */ noop: any = () => {
  // empty method
};

export const /** @type {?} */ TD_DATA_TABLE_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TdDataTableComponent),
  multi: true,
};
export type TdDataTableSortingOrder = number;
export let TdDataTableSortingOrder: any = {};
TdDataTableSortingOrder.Ascending =  /** @type {?} */(( <any>'ASC'));
TdDataTableSortingOrder.Descending =  /** @type {?} */(( <any>'DESC'));
TdDataTableSortingOrder[TdDataTableSortingOrder.Ascending] = "Ascending";
TdDataTableSortingOrder[TdDataTableSortingOrder.Descending] = "Descending";


export interface ITdDataTableColumn {
  name: string;
  label: string;
  tooltip?: string;
  numeric?: boolean;
  format?: (value: any) => any;
  nested?: boolean;
  sortable?: boolean;
  hidden?: boolean;
  filter?: boolean;
}

export interface ITdDataTableSelectEvent {
  row: any;
  selected: boolean;
}

export interface ITdDataTableSelectAllEvent {
  rows: any[];
  selected: boolean;
}

export interface ITdDataTableRowClickEvent {
  row: any;
}
export type TdDataTableArrowKeyDirection = number;
export let TdDataTableArrowKeyDirection: any = {};
TdDataTableArrowKeyDirection.Ascending =  /** @type {?} */(( <any>'ASC'));
TdDataTableArrowKeyDirection.Descending =  /** @type {?} */(( <any>'DESC'));
TdDataTableArrowKeyDirection[TdDataTableArrowKeyDirection.Ascending] = "Ascending";
TdDataTableArrowKeyDirection[TdDataTableArrowKeyDirection.Descending] = "Descending";

export class TdDataTableComponent implements ControlValueAccessor, AfterContentInit {
/**
 * Implemented as part of ControlValueAccessor.
 */
private _value: any[] = [];
/**
 * Callback registered via registerOnChange (ControlValueAccessor)
 */
private _onChangeCallback: (_: any) => void = noop;
/**
 * internal attributes
 */
private _data: any[];
private _columns: ITdDataTableColumn[];
private _selectable: boolean = false;
private _clickable: boolean = false;
private _multiple: boolean = true;
private _allSelected: boolean = false;
private _indeterminate: boolean = false;
/**
 * sorting
 */
private _sortable: boolean = false;
private _sortBy: ITdDataTableColumn;
private _sortOrder: TdDataTableSortingOrder = TdDataTableSortingOrder.Ascending;
/**
 * shift select
 */
private _lastSelectedIndex: number = -1;
private _selectedBeforeLastIndex: number = -1;
private _lastArrowKeyDirection: TdDataTableArrowKeyDirection;
/**
 * template fetching support
 */
private _templateMap: Map<string, TemplateRef<any>> = new Map<string, TemplateRef<any>>();
   _templates: QueryList<TdDataTableTemplateDirective>;

   _rows: QueryList<TdDataTableRowComponent>;
/**
 * Returns true if all values are selected.
 * @return {?}
 */
get allSelected(): boolean {
    return this._allSelected;
  }
/**
 * Returns true if all values are not deselected
 * and atleast one is.
 * @return {?}
 */
get indeterminate(): boolean {
    return this._indeterminate;
  }
/**
 * Implemented as part of ControlValueAccessor.
 * @param {?} v
 * @return {?}
 */
set value(v: any) {
    if (v !== this._value) {
      this._value = v;
      this._onChangeCallback(v);
      this.refresh();
    }
  }
/**
 * @return {?}
 */
get value(): any { return this._value; }
/**
 * uniqueId?: string
 * Allows selection by [uniqueId] property.
 */
uniqueId: string;
/**
 * data?: {[key: string]: any}[]
 * Sets the data to be rendered as rows.
 * @param {?} data
 * @return {?}
 */
set data(data: any[]) {
    this._data = data;
    this.refresh();
  }
/**
 * @return {?}
 */
get data(): any[] {
    return this._data;
  }
/**
 * columns?: ITdDataTableColumn[]
 * Sets additional column configuration. [ITdDataTableColumn.name] has to exist in [data] as key.
 * Defaults to [data] keys.
 * @param {?} cols
 * @return {?}
 */
set columns(cols: ITdDataTableColumn[]) {
    this._columns = cols;
  }
/**
 * @return {?}
 */
get columns(): ITdDataTableColumn[] {
    if (this._columns) {
      return this._columns;
    }

    if (this.hasData) {
      this._columns = [];
      // if columns is undefined, use key in [data] rows as name and label for column headers.
      let /** @type {?} */ row: any = this._data[0];
      Object.keys(row).forEach((k: string) => {
        if (!this._columns.find((c: any) => c.name === k)) {
          this._columns.push({ name: k, label: k });
        }
      });
      return this._columns;
    } else {
      return [];
    }
  }
/**
 * selectable?: boolean
 * Enables row selection events, hover and selected row states.
 * Defaults to 'false'
 * @param {?} selectable
 * @return {?}
 */
set selectable(selectable: string | boolean) {
    this._selectable = selectable !== '' ? (selectable === 'true' || selectable === true) : true;
  }
/**
 * @return {?}
 */
get isSelectable(): boolean {
    return this._selectable;
  }
/**
 * clickable?: boolean
 * Enables row click events, hover.
 * Defaults to 'false'
 * @param {?} clickable
 * @return {?}
 */
set clickable(clickable: string | boolean) {
    this._clickable = clickable !== '' ? (clickable === 'true' || clickable === true) : true;
  }
/**
 * @return {?}
 */
get isClickable(): boolean {
    return this._clickable;
  }
/**
 * multiple?: boolean
 * Enables multiple row selection. [selectable] needs to be enabled.
 * Defaults to 'false'
 * @param {?} multiple
 * @return {?}
 */
set multiple(multiple: string | boolean) {
    this._multiple = multiple !== '' ? (multiple === 'true' || multiple === true) : true;
  }
/**
 * @return {?}
 */
get isMultiple(): boolean {
    return this._multiple;
  }
/**
 * sortable?: boolean
 * Enables sorting events, sort icons and active column states.
 * Defaults to 'false'
 * @param {?} sortable
 * @return {?}
 */
set sortable(sortable: string | boolean) {
    this._sortable = sortable !== '' ? (sortable === 'true' || sortable === true) : true;
  }
/**
 * @return {?}
 */
get isSortable(): boolean {
    return this._sortable;
  }
/**
 * sortBy?: string
 * Sets the active sort column. [sortable] needs to be enabled.
 * @param {?} columnName
 * @return {?}
 */
set sortBy(columnName: string) {
    if (!columnName) {
      return;
    }
    const /** @type {?} */ column: ITdDataTableColumn = this.columns.find((c: any) => c.name === columnName);
    if (!column) {
      throw new Error('[sortBy] must be a valid column name');
    }

    this._sortBy = column;
  }
/**
 * @return {?}
 */
get sortByColumn(): ITdDataTableColumn {
    return this._sortBy;
  }
/**
 * sortOrder?: ['ASC' | 'DESC'] or TdDataTableSortingOrder
 * Sets the sort order of the [sortBy] column. [sortable] needs to be enabled.
 * Defaults to 'ASC' or TdDataTableSortingOrder.Ascending
 * @param {?} order
 * @return {?}
 */
set sortOrder(order: 'ASC' | 'DESC') {
    let /** @type {?} */ sortOrder: string = order ? order.toUpperCase() : 'ASC';
    if (sortOrder !== 'DESC' && sortOrder !== 'ASC') {
      throw new Error('[sortOrder] must be empty, ASC or DESC');
    }

    this._sortOrder = sortOrder === 'ASC' ?
      TdDataTableSortingOrder.Ascending : TdDataTableSortingOrder.Descending;
  }
/**
 * @return {?}
 */
get sortOrderEnum(): TdDataTableSortingOrder {
    return this._sortOrder;
  }
/**
 * @return {?}
 */
get hasData(): boolean {
    return this._data && this._data.length > 0;
  }
/**
 * sortChange?: function
 * Event emitted when the column headers are clicked. [sortable] needs to be enabled.
 * Emits an [ITdDataTableSortChangeEvent] implemented object.
 */
onSortChange: EventEmitter<ITdDataTableSortChangeEvent> =
                                      new EventEmitter<ITdDataTableSortChangeEvent>();
/**
 * rowSelect?: function
 * Event emitted when a row is selected/deselected. [selectable] needs to be enabled.
 * Emits an [ITdDataTableSelectEvent] implemented object.
 */
onRowSelect: EventEmitter<ITdDataTableSelectEvent> = new EventEmitter<ITdDataTableSelectEvent>();
/**
 * rowClick?: function
 * Event emitted when a row is clicked.
 * Emits an [ITdDataTableRowClickEvent] implemented object.
 */
onRowClick: EventEmitter<ITdDataTableRowClickEvent> = new EventEmitter<ITdDataTableRowClickEvent>();
/**
 * selectAll?: function
 * Event emitted when all rows are selected/deselected by the all checkbox. [selectable] needs to be enabled.
 * Emits an [ITdDataTableSelectAllEvent] implemented object.
 */
onSelectAll: EventEmitter<ITdDataTableSelectAllEvent> =
                                    new EventEmitter<ITdDataTableSelectAllEvent>();
/**
 * @param {?} _document
 * @param {?} _changeDetectorRef
 */
constructor(
private _document: any,
private _changeDetectorRef: ChangeDetectorRef) {}
/**
 * Loads templates and sets them in a map for faster access.
 * @return {?}
 */
ngAfterContentInit(): void {
    for (let /** @type {?} */ i: number = 0; i < this._templates.toArray().length; i++) {
      this._templateMap.set(
        this._templates.toArray()[i].tdDataTableTemplate,
        this._templates.toArray()[i].templateRef,
      );
    }
  }
/**
 * @param {?} column
 * @param {?} value
 * @return {?}
 */
getCellValue(column: ITdDataTableColumn, value: any): string {
    if (column.nested === undefined || column.nested) {
      return this._getNestedValue(column.name, value);
    }
    return value[column.name];
  }
/**
 * Getter method for template references
 * @param {?} name
 * @return {?}
 */
getTemplateRef(name: string): TemplateRef<any> {
     return this._templateMap.get(name);
   }
/**
 * Clears model (ngModel) of component by removing all values in array.
 * @return {?}
 */
clearModel(): void {
    this._value.splice(0, this._value.length);
  }
/**
 * Refreshes data table and rerenders [data] and [columns]
 * @return {?}
 */
refresh(): void {
    this._calculateCheckboxState();
    this._changeDetectorRef.markForCheck();
  }
/**
 * Selects or clears all rows depending on 'checked' value.
 * @param {?} checked
 * @return {?}
 */
selectAll(checked: boolean): void {
    if (checked) {
      this._data.forEach((row: any) => {
        // skiping already selected rows
        if (!this.isRowSelected(row)) {
          this._value.push(row);
        }
      });
      this._allSelected = true;
      this._indeterminate = true;
    } else {
      this.clearModel();
      this._allSelected = false;
      this._indeterminate = false;
    }
    this.onSelectAll.emit({rows: this._value, selected: checked});
  }
/**
 * Checks if row is selected
 * @param {?} row
 * @return {?}
 */
isRowSelected(row: any): boolean {
    // if selection is done by a [uniqueId] it uses it to compare, else it compares by reference.
    if (this.uniqueId) {
      return this._value ? this._value.filter((val: any) => {
        return val[this.uniqueId] === row[this.uniqueId];
      }).length > 0 : false;
    }
    return this._value ? this._value.indexOf(row) > -1 : false;
  }
/**
 * Selects or clears a row depending on 'checked' value if the row 'isSelectable'
 * handles cntrl clicks and shift clicks for multi-select
 * @param {?} row
 * @param {?} event
 * @param {?} currentSelected
 * @return {?}
 */
select(row: any, event: Event, currentSelected: number): void {
    if (this.isSelectable) {
      this.blockEvent(event);
      this._doSelection(row);

      // Check to see if Shift key is selected and need to select everything in between
      let /** @type {?} */ mouseEvent: MouseEvent = /** @type {?} */(( event as MouseEvent));
      if (this.isMultiple && mouseEvent && mouseEvent.shiftKey && this._lastSelectedIndex > -1) {
        let /** @type {?} */ firstIndex: number = currentSelected;
        let /** @type {?} */ lastIndex: number = this._lastSelectedIndex;
        if (currentSelected > this._lastSelectedIndex) {
          firstIndex = this._lastSelectedIndex;
          lastIndex = currentSelected;
        }
        for (let /** @type {?} */ i: number = firstIndex + 1; i < lastIndex; i++) {
          this._doSelection(this._data[i]);
        }
      }
      // set the last selected attribute unless the last selected unchecked a row
      if (this.isRowSelected(this._data[currentSelected])) {
        this._selectedBeforeLastIndex = this._lastSelectedIndex;
        this._lastSelectedIndex = currentSelected;
      } else {
        this._lastSelectedIndex = this._selectedBeforeLastIndex;
      }
      // everything is unselected so start over
      if (!this._indeterminate && !this._allSelected) {
        this._lastSelectedIndex = -1;
      }
    }
  }
/**
 * Overrides the onselectstart method of the document so other text on the page
 * doesn't get selected when doing shift selections.
 * @return {?}
 */
disableTextSelection(): void {
    if (this._document) {
      this._document.onselectstart = function(): boolean {
        return false;
      };
    }
  }
/**
 * Resets the original onselectstart method.
 * @return {?}
 */
enableTextSelection(): void {
    if (this._document) {
      this._document.onselectstart = undefined;
    }
  }
/**
 * emits the onRowClickEvent when a row is clicked
 * if clickable is true and selectable is false then select the row
 * @param {?} row
 * @param {?} event
 * @param {?} currentSelected
 * @return {?}
 */
handleRowClick(row: any, event: Event, currentSelected: number): void {
    if (this.isClickable) {
      this.onRowClick.emit({row: row});
    }
  }
/**
 * Method handle for sort click event in column headers.
 * @param {?} column
 * @return {?}
 */
handleSort(column: ITdDataTableColumn): void {
    if (this._sortBy === column) {
      this._sortOrder = this._sortOrder === TdDataTableSortingOrder.Ascending ?
        TdDataTableSortingOrder.Descending : TdDataTableSortingOrder.Ascending;
    } else {
      this._sortBy = column;
      this._sortOrder = TdDataTableSortingOrder.Ascending;
    }
    this.onSortChange.next({ name: this._sortBy.name, order: this._sortOrder });
  }
/**
 * Handle all keyup events when focusing a data table row
 * @param {?} event
 * @param {?} row
 * @param {?} index
 * @return {?}
 */
_rowKeyup(event: KeyboardEvent, row: any, index: number): void {
    let /** @type {?} */ length: number;
    let /** @type {?} */ rows: TdDataTableRowComponent[];
    switch (event.keyCode) {
      case ENTER:
      case SPACE:
        /** if user presses enter or space, the row should be selected */
        this.select(row, event, index);
        break;
      case UP_ARROW:
        rows = this._rows.toArray();
        length = rows.length;

        // check to see if changing direction and need to toggle the current row
        if (this._lastArrowKeyDirection === TdDataTableArrowKeyDirection.Descending) {
          index++;
        }
        /** 
         * if users presses the up arrow, we focus the prev row 
         * unless its the first row, then we move to the last row
         */
        if (index === 0) {
          if (!event.shiftKey) {
            rows[length - 1].focus();
          }
        } else {
          rows[index - 1].focus();
        }
        this.blockEvent(event);
        if (this.isMultiple && event.shiftKey) {
          this._doSelection(this._data[index - 1]);
          // if the checkboxes are all unselected then start over otherwise handle changing direction
          this._lastArrowKeyDirection = (!this._allSelected && !this._indeterminate) ? undefined : TdDataTableArrowKeyDirection.Ascending;
        }
        break;
      case DOWN_ARROW:
        rows = this._rows.toArray();
        length = rows.length;

        // check to see if changing direction and need to toggle the current row
        if (this._lastArrowKeyDirection === TdDataTableArrowKeyDirection.Ascending) {
          index--;
        }
        /** 
         * if users presses the down arrow, we focus the next row 
         * unless its the last row, then we move to the first row
         */
        if (index === (length - 1)) {
          if (!event.shiftKey) {
           rows[0].focus();
          }
        } else {
          rows[index + 1].focus();
        }
        this.blockEvent(event);
        if (this.isMultiple && event.shiftKey) {
          this._doSelection(this._data[index + 1]);
          // if the checkboxes are all unselected then start over otherwise handle changing direction
          this._lastArrowKeyDirection = (!this._allSelected && !this._indeterminate) ? undefined : TdDataTableArrowKeyDirection.Descending;
        }
        break;
      default:
        // default
    }
  }
/**
 * Method to prevent the default events
 * @param {?} event
 * @return {?}
 */
blockEvent(event: Event): void {
    event.preventDefault();
  }
/**
 * Implemented as part of ControlValueAccessor.
 * @param {?} value
 * @return {?}
 */
writeValue(value: any): void {
    this.value = value;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: any): void {
    this.onChange = fn;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onChange = (_: any) => noop;
  onTouched = () => noop;
/**
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
private _getNestedValue(name: string, value: any): string {
    if (!(value instanceof Object) || !name) {
      return value;
    }
    if (name.indexOf('.') > -1) {
      let /** @type {?} */ splitName: string[] = name.split(/\.(.+)/, 2);
      return this._getNestedValue(splitName[1], value[splitName[0]]);
    } else {
      return value[name];
    }
  }
/**
 * Does the actual Row Selection
 * @param {?} row
 * @return {?}
 */
private _doSelection(row: any): void {
    let /** @type {?} */ wasSelected: boolean = this.isRowSelected(row);
    if (!this._multiple) {
      this.clearModel();
    }
    if (!wasSelected) {
      this._value.push(row);
    } else {
      // if selection is done by a [uniqueId] it uses it to compare, else it compares by reference.
      if (this.uniqueId) {
        row = this._value.filter((val: any) => {
          return val[this.uniqueId] === row[this.uniqueId];
        })[0];
      }
      let /** @type {?} */ index: number = this._value.indexOf(row);
      if (index > -1) {
        this._value.splice(index, 1);
      }
    }
    this._calculateCheckboxState();
    this.onRowSelect.emit({row: row, selected: this.isRowSelected(row)});
    this.onChange(this._value);
  }
/**
 * Calculate all the state of all checkboxes
 * @return {?}
 */
private _calculateCheckboxState(): void {
    this._calculateAllSelected();
    this._calculateIndeterminate();
  }
/**
 * Checks if all visible rows are selected.
 * @return {?}
 */
private _calculateAllSelected(): void {
    const /** @type {?} */ match: string =
      this._data ? this._data.find((d: any) => !this.isRowSelected(d)) : true;
    this._allSelected = typeof match === 'undefined';
  }
/**
 * Checks if all visible rows are selected.
 * @return {?}
 */
private _calculateIndeterminate(): void {
    this._indeterminate = false;
    if (this._data) {
      for (let /** @type {?} */ row of this._data) {
        if (!this.isRowSelected(row)) {
          continue;
        }
        this._indeterminate = true;
      }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  providers: [ TD_DATA_TABLE_CONTROL_VALUE_ACCESSOR ],
  selector: 'td-data-table',
  styleUrls: ['./data-table.component.css' ],
  templateUrl: './data-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [DOCUMENT, ] }, ]},
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_templates': [{ type: ContentChildren, args: [TdDataTableTemplateDirective, ] },],
'_rows': [{ type: ViewChildren, args: [TdDataTableRowComponent, ] },],
'value': [{ type: Input },],
'uniqueId': [{ type: Input, args: ['uniqueId', ] },],
'data': [{ type: Input, args: ['data', ] },],
'columns': [{ type: Input, args: ['columns', ] },],
'selectable': [{ type: Input, args: ['selectable', ] },],
'clickable': [{ type: Input, args: ['clickable', ] },],
'multiple': [{ type: Input, args: ['multiple', ] },],
'sortable': [{ type: Input, args: ['sortable', ] },],
'sortBy': [{ type: Input, args: ['sortBy', ] },],
'sortOrder': [{ type: Input, args: ['sortOrder', ] },],
'onSortChange': [{ type: Output, args: ['sortChange', ] },],
'onRowSelect': [{ type: Output, args: ['rowSelect', ] },],
'onRowClick': [{ type: Output, args: ['rowClick', ] },],
'onSelectAll': [{ type: Output, args: ['selectAll', ] },],
};
}

function TdDataTableComponent_tsickle_Closure_declarations() {
/** @type {?} */
TdDataTableComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TdDataTableComponent.ctorParameters;
/** @type {?} */
TdDataTableComponent.propDecorators;
/**
 * Implemented as part of ControlValueAccessor.
 * @type {?}
 */
TdDataTableComponent.prototype._value;
/**
 * Callback registered via registerOnChange (ControlValueAccessor)
 * @type {?}
 */
TdDataTableComponent.prototype._onChangeCallback;
/**
 * internal attributes
 * @type {?}
 */
TdDataTableComponent.prototype._data;
/** @type {?} */
TdDataTableComponent.prototype._columns;
/** @type {?} */
TdDataTableComponent.prototype._selectable;
/** @type {?} */
TdDataTableComponent.prototype._clickable;
/** @type {?} */
TdDataTableComponent.prototype._multiple;
/** @type {?} */
TdDataTableComponent.prototype._allSelected;
/** @type {?} */
TdDataTableComponent.prototype._indeterminate;
/**
 * sorting
 * @type {?}
 */
TdDataTableComponent.prototype._sortable;
/** @type {?} */
TdDataTableComponent.prototype._sortBy;
/** @type {?} */
TdDataTableComponent.prototype._sortOrder;
/**
 * shift select
 * @type {?}
 */
TdDataTableComponent.prototype._lastSelectedIndex;
/** @type {?} */
TdDataTableComponent.prototype._selectedBeforeLastIndex;
/** @type {?} */
TdDataTableComponent.prototype._lastArrowKeyDirection;
/**
 * template fetching support
 * @type {?}
 */
TdDataTableComponent.prototype._templateMap;
/** @type {?} */
TdDataTableComponent.prototype._templates;
/** @type {?} */
TdDataTableComponent.prototype._rows;
/**
 * uniqueId?: string
 * Allows selection by [uniqueId] property.
 * @type {?}
 */
TdDataTableComponent.prototype.uniqueId;
/**
 * sortChange?: function
 * Event emitted when the column headers are clicked. [sortable] needs to be enabled.
 * Emits an [ITdDataTableSortChangeEvent] implemented object.
 * @type {?}
 */
TdDataTableComponent.prototype.onSortChange;
/**
 * rowSelect?: function
 * Event emitted when a row is selected/deselected. [selectable] needs to be enabled.
 * Emits an [ITdDataTableSelectEvent] implemented object.
 * @type {?}
 */
TdDataTableComponent.prototype.onRowSelect;
/**
 * rowClick?: function
 * Event emitted when a row is clicked.
 * Emits an [ITdDataTableRowClickEvent] implemented object.
 * @type {?}
 */
TdDataTableComponent.prototype.onRowClick;
/**
 * selectAll?: function
 * Event emitted when all rows are selected/deselected by the all checkbox. [selectable] needs to be enabled.
 * Emits an [ITdDataTableSelectAllEvent] implemented object.
 * @type {?}
 */
TdDataTableComponent.prototype.onSelectAll;
/** @type {?} */
TdDataTableComponent.prototype.onChange;
/** @type {?} */
TdDataTableComponent.prototype.onTouched;
/** @type {?} */
TdDataTableComponent.prototype._document;
/** @type {?} */
TdDataTableComponent.prototype._changeDetectorRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
